import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef } from "@mui/x-data-grid-premium";
import type { FC } from "react";
import {
  prefetchSystemById,
  prefetchSystemForm,
  prefetchSystemsGridView,
  useGetSystemForm,
  useGetSystemsGridView,
} from "../../api/systems";
import { DynamicGrid } from "../../dynamicGrid";
import type { GridViewSystem } from "../../api/systems.types";
import { criticalityColumn } from "../../classification/criticalityColumn";
import { classificationScoresColumn } from "../../classification/classificationScoresColumn";
import { classificationTemplateQueryOptions } from "../../api/classificationTemplate";
import { prefetch } from "../../api/prefetch";
import { getCurrentOrganisation, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { SystemIcon2, RegistrationControllerIcon3 } from "../../shared/icons";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { EmptyState } from "../../shared/EmptyState";
import { personalDataControllersDisplayListQueryOptions } from "../../api/personalDataControllers";
import { selectPartnerOptionsQueryOptions } from "../../api/partners";
import { Grid } from "../../shared/Grid";
import { SystemsGridToolbar } from "./SystemsGridToolbar";
import { ConnectedRegistrationsSubDrawer } from "./SystemDrawer/ConnectedRegistrationsSubdrawer";
import { SystemHistoryDrawer } from "./SystemDrawer/SystemHistoryDrawer";
import { SystemInlineFormDrawer } from "./SystemDrawer/SystemsDrawer";
import { SystemActionMenu } from "./SystemDrawer/SystemsDrawerHeader";
import { useQuery } from "@tanstack/react-query";

const SystemsGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.Systems");
  const { t } = useTranslation();
  const systemsGridView = useGetSystemsGridView();
  const systemForm = useGetSystemForm();
  const usersQueryResult = useGetUsersFromCurrentOrganisation();
  const personalDataControllersQueryResult = useQuery(personalDataControllersDisplayListQueryOptions());
  const partnersQueryResult = useQuery(selectPartnerOptionsQueryOptions());
  const { classificationEnabled } = useCurrentOrganisation();
  const templateQuery = useQuery({ ...classificationTemplateQueryOptions(), enabled: classificationEnabled });
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Grid
      rowsData={systemsGridView.data?.systems}
      showArchivedFilter
      form={systemForm.data}
      getRowId={getRowId}
      asyncQuestionCardData={{
        usersQueryData: usersQueryResult.data,
        personalDataControllersQueryData: personalDataControllersQueryResult.data,
        partnersQueryData: partnersQueryResult.data,
      }}
      mainColumnProps={{
        headerName: t("SystemsGrid.NameHeader"),
        renderLabel: ({ name }) => name,
        renderIcon: ({ iconProps }) => <SystemIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => [
        ...(classificationEnabled
          ? [
              classificationScoresColumn<GridViewSystem>(
                t,
                templateQuery.data?.template,
                row => row.classification?.override?.scores ?? []
              ),
              criticalityColumn<GridViewSystem>(t, row => row.classification?.criticality),
            ]
          : []),
        ...questionCardColumns,
        connectedControllerRegistrationColumn(t),
        connectedProcessorRegistrationColumn(t),
      ]}
      onRowSelect={selected => navigate(paths.systemsInline(selected))}
      Toolbar={SystemsGridToolbar}
      Footer={SystemsGridFooter}
      NoRowsOverlay={NoSystemOverlay}
      texts={{ searchFieldPlaceholder: t("SystemGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <SystemActionMenu
          systemId={row.systemId}
          systemName={row.name}
          archivedAt={row.archivedAt}
          onDeleted={() => {
            if (selectedRowId === row.systemId) navigate(paths.systemsGrid());
          }}
        />
      )}
    />
  );
};

const getRowId = (row: GridViewSystem) => row.systemId;

const SystemsGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="SystemGrid.NumberOfRows" />;

const NoSystemOverlay = () => {
  return <EmptyState labelKey="SystemGrid.NoRows" />;
};

const connectedControllerRegistrationColumn = (
  t: (translation: TranslationKey) => string
): GridColDef<GridViewSystem> => ({
  ...DynamicGrid.requiredColDef({
    headerName: t("SystemGrid.ConnectedControllerRegistrationHeader"),
    field: "connectedControllerRegistrations",
  }),
  width: 45,
  sortable: false,
  /** The value getter value is used for sorting and searching */
  valueGetter: (_, row) =>
    row.connectedControllerRegistrations.registrations
      .map(registration => registration.name)
      .join(Grid.VALUE_GETTER_DELIMITER),
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <ConnectedControllerRegistrationCell
        systemId={row.systemId}
        totalConnectedRegistrations={row.connectedControllerRegistrations.totalCount}
      />
    </DynamicGrid.Cell>
  ),
});

const ConnectedControllerRegistrationCell: FC<{
  systemId: string;
  totalConnectedRegistrations: number;
}> = ({ systemId, totalConnectedRegistrations }) => {
  const { paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.OpenSubDrawerLink
      Icon={RegistrationControllerIcon3}
      label={totalConnectedRegistrations}
      to={paths.systemsConnectedRegistrations(systemId)}
    />
  );
};

const connectedProcessorRegistrationColumn = (
  t: (translation: TranslationKey) => string
): GridColDef<GridViewSystem> => ({
  ...DynamicGrid.requiredColDef({
    headerName: t("SystemGrid.ConnectedProcessorRegistrationHeader"),
    field: "connectedProcessorRegistrations",
  }),
  width: 45,
  sortable: false,
  /** The value getter value is used for sorting and searching */
  valueGetter: (_, row) =>
    row.connectedProcessorRegistrations.registrations
      .map(registration => registration.name)
      .join(Grid.VALUE_GETTER_DELIMITER),
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <ConnectedProcessorRegistrationCell
        systemId={row.systemId}
        totalConnectedRegistrations={row.connectedProcessorRegistrations.totalCount}
      />
    </DynamicGrid.Cell>
  ),
});

const ConnectedProcessorRegistrationCell: FC<{
  systemId: string;
  totalConnectedRegistrations: number;
}> = ({ systemId, totalConnectedRegistrations }) => {
  const { paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.OpenSubDrawerLink
      Icon={RegistrationControllerIcon3}
      label={totalConnectedRegistrations}
      to={paths.systemsConnectedProcessorRegistrations(systemId)}
    />
  );
};

export const systemGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <SystemsGridPage />,
  rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetchSystemById(rowId),
  gridPrefetcher: () => void prefetchSystemsGridViewAndForm(),
  rowDrawerRoutes: [
    { index: true, element: <SystemInlineFormDrawer /> },
    { path: "registrations", element: <ConnectedRegistrationsSubDrawer registrationVariant="Controller" /> },
    { path: "registrations-processor", element: <ConnectedRegistrationsSubDrawer registrationVariant="Processor" /> },
    { path: "history", element: <SystemHistoryDrawer /> },
  ],
});

export const prefetchSystemsGridViewAndForm = () => {
  prefetchSystemsGridView();
  prefetchSystemForm();
  getCurrentOrganisation().then(currentOrganisation => {
    if (currentOrganisation?.classificationEnabled) prefetch(classificationTemplateQueryOptions());
  });
};
